import { useEffect, useRef } from "react";
import { formatAmount } from "../../helpers/shared";

// Discount shown in the modal. MUST match the Stripe coupon behind
// STRIPE_EXIT_OFFER_COUPON (percent_off, duration: once) — the coupon is what
// actually charges the discounted amount; this constant is only the display.
export const EXIT_DISCOUNT_PCT = 40;

const SHOWN_KEY = "funnel_exit_offer_shown";

/**
 * Fires `onExit` once when the visitor signals they're leaving:
 *  - mobile: the back button (the only reliable "exit" on touch) — we push a
 *    history guard so the first back pops here and shows the offer instead of
 *    navigating away; a second back still leaves.
 *  - desktop: the mouse leaving through the top of the window.
 * Guarded to fire at most once per session so we never nag.
 */
export const useExitIntent = (
  active: boolean,
  onExit: (source: string) => void
) => {
  const firedRef = useRef(false);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  useEffect(() => {
    if (!active) return;

    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(SHOWN_KEY) === "1";
    } catch {}
    if (alreadyShown) return;

    const fire = (source: string) => {
      if (firedRef.current) return;
      firedRef.current = true;
      try {
        sessionStorage.setItem(SHOWN_KEY, "1");
      } catch {}
      onExitRef.current(source);
    };

    // Mobile: arm a history guard, then catch the back press.
    try {
      window.history.pushState({ exitGuard: true }, "");
    } catch {}
    const onPop = () => fire("back");

    // Desktop: pointer left through the top edge of the viewport.
    const onMouseOut = (e: MouseEvent) => {
      if (!e.relatedTarget && e.clientY <= 0) fire("mouseleave");
    };

    window.addEventListener("popstate", onPop);
    document.addEventListener("mouseout", onMouseOut);
    return () => {
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [active]);
};

type ModalProps = {
  open: boolean;
  annualAmount?: number;
  onClaim: () => void;
  onDismiss: () => void;
};

export const ExitOfferModal = ({
  open,
  annualAmount,
  onClaim,
  onDismiss,
}: ModalProps) => {
  if (!open) return null;

  const discounted =
    typeof annualAmount === "number"
      ? Math.round(annualAmount * (1 - EXIT_DISCOUNT_PCT / 100))
      : undefined;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/50 px-4 pb-4 sm:pb-0"
      role="dialog"
      aria-modal="true"
      aria-label="Special offer"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl p-6 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-4xl mb-2">🎁</div>
        <h2 className="text-2xl font-bold text-black leading-tight mb-1">
          Wait — a gift before you go
        </h2>
        <p className="text-main-grey-800 text-sm mb-4">
          Unlock the full library for{" "}
          <span className="text-main-blue font-bold">
            {EXIT_DISCOUNT_PCT}% less
          </span>{" "}
          — your first year only.
        </p>

        <div className="flex items-center justify-center gap-2 mb-1">
          <span className="text-3xl font-bold text-black">
            {discounted ? formatAmount(discounted) : "—"}
          </span>
          {typeof annualAmount === "number" && (
            <span className="text-main-grey-800 text-lg line-through">
              {formatAmount(annualAmount)}
            </span>
          )}
        </div>
        <p className="text-main-grey-800 text-xxs mb-5">
          first year, then{" "}
          {typeof annualAmount === "number" ? formatAmount(annualAmount) : "—"}
          /year · cancel anytime
        </p>

        <button
          onClick={onClaim}
          className="w-full bg-main-blue text-white font-semibold py-4 rounded-2xl text-lg active:scale-[0.98] transition-transform"
        >
          Claim my {EXIT_DISCOUNT_PCT}% off
        </button>
        <button
          onClick={onDismiss}
          className="w-full text-main-grey-800 text-sm py-3 mt-1"
        >
          No thanks, I'll pay full price
        </button>
      </div>
    </div>
  );
};
